import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

export function BillingSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useBilling();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    async function poll(n = 0) {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed"); return;
        }
      } catch (e) {
        console.warn("Status poll failed:", e?.message || e);
      }
      setAttempts(n + 1);
      if (n < 12) setTimeout(() => poll(n + 1), 2000);
      else setStatus("failed");
    }
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[70vh] grid place-items-center">
      <Card className="p-10 rounded-2xl max-w-md text-center border-border" data-testid="billing-success">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
            <h2 className="font-display text-2xl mt-4">Confirming your subscription…</h2>
            <p className="text-sm text-stone-500 mt-1">Attempt {attempts + 1} · this takes just a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2 className="font-display text-2xl mt-4">Welcome to Pro</h2>
            <p className="text-sm text-stone-500 mt-2">Your 7-day trial is active. Create unlimited trips, assign travelers, and vault everything.</p>
            <Button onClick={() => nav("/")} className="mt-6 rounded-full bg-primary text-primary-foreground" data-testid="go-to-dashboard-btn">
              Go to my trips <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-14 h-14 rounded-full bg-destructive/10 text-destructive grid place-items-center mx-auto">
              <XCircle className="w-7 h-7" />
            </div>
            <h2 className="font-display text-2xl mt-4">We couldn't confirm the payment</h2>
            <p className="text-sm text-stone-500 mt-2">If you were charged, your plan will update within a few minutes.</p>
            <Link to="/pricing" className="mt-6 inline-block text-primary" data-testid="back-to-pricing">Back to pricing</Link>
          </>
        )}
      </Card>
    </div>
  );
}

export function BillingCancel() {
  return (
    <div className="min-h-[70vh] grid place-items-center">
      <Card className="p-10 rounded-2xl max-w-md text-center border-border" data-testid="billing-cancel">
        <div className="w-14 h-14 rounded-full bg-muted text-stone-500 grid place-items-center mx-auto">
          <XCircle className="w-7 h-7" />
        </div>
        <h2 className="font-display text-2xl mt-4">Checkout canceled</h2>
        <p className="text-sm text-stone-500 mt-2">No worries — your Free plan is still active whenever you're ready.</p>
        <Link to="/pricing" className="mt-6 inline-block text-primary font-medium" data-testid="try-again-link">Try again</Link>
      </Card>
    </div>
  );
}
